import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Purpose-built mobile sheet for the sitebot chat.
 *
 * The generic `MobileSheet` freezes its height at `window.innerHeight` on mount,
 * which is exactly the thing that breaks when the on-screen keyboard opens: the
 * sheet keeps its full height, the layout viewport scrolls under the keyboard,
 * and the composer ends up hidden (the "keyboard glitches" bug). This sheet is
 * sized from `window.visualViewport` instead — the only box that reports what is
 * actually on screen — so the composer stays parked right above the keyboard.
 *
 * Sizing contract (all CSS, driven by two custom properties this component
 * keeps up to date on <html>):
 *   --chat-vvh  visual viewport height   (fallback 100dvh)
 *   --chat-vvt  visual viewport offsetTop (fallback 0)
 *   top    = --chat-vvt + gap
 *   height = --chat-vvh - gap            → the sheet's bottom edge is exactly
 *                                          the top of the keyboard
 * The gap shrinks while the keyboard is up so the message list keeps its room.
 *
 * The sheet itself never scrolls; the message list inside it is the only
 * scroller, and the page behind is pinned (position: fixed) so iOS can't drag
 * the document out from under a fixed element.
 */

export interface AgentChatSheetProps {
  open: boolean;
  onClose: () => void;
  /**
   * Everything below the drag grip. Rendered as a single flex child that owns
   * the remaining height, so the caller can keep one Tabs root around both the
   * room switcher and the message list.
   */
  children: React.ReactNode;
}

/** how far you have to drag the header down before it closes */
const DRAG_CLOSE_PX = 96;
/** viewport shrink that means "the keyboard is up" */
const KEYBOARD_MIN_PX = 120;

const AgentChatSheet: React.FC<AgentChatSheetProps> = ({ open, onClose, children }) => {
  const [rendered, setRendered] = useState(open);
  const [shown, setShown] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<number | null>(null);
  const dragDeltaRef = useRef(0);
  const restHeightRef = useRef(0);

  // ── mount / unmount around the slide transition ──
  useEffect(() => {
    if (open) {
      setRendered(true);
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const id = setTimeout(() => setRendered(false), 260);
    return () => clearTimeout(id);
  }, [open]);

  // ── visual viewport → CSS custom properties ──
  useEffect(() => {
    if (!rendered) return undefined;
    const root = document.documentElement;
    const vv = window.visualViewport;
    restHeightRef.current = vv ? vv.height : window.innerHeight;
    let raf = 0;

    const apply = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const h = vv ? vv.height : window.innerHeight;
        const t = vv ? vv.offsetTop : 0;
        root.style.setProperty("--chat-vvh", `${Math.round(h)}px`);
        root.style.setProperty("--chat-vvt", `${Math.round(t)}px`);
        // Android resizes the layout viewport too, so comparing against
        // innerHeight isn't enough — track the tallest viewport seen instead.
        if (h > restHeightRef.current) restHeightRef.current = h;
        setKeyboardOpen(restHeightRef.current - h > KEYBOARD_MIN_PX);
      });
    };

    apply();
    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      root.style.removeProperty("--chat-vvh");
      root.style.removeProperty("--chat-vvt");
    };
  }, [rendered]);

  // ── pin the page behind the sheet ──
  // `overflow: hidden` alone is not enough: iOS Safari still scrolls the
  // document to reveal a focused input, which drags fixed elements off screen.
  // Pinning the body removes the scrollable document entirely.
  useEffect(() => {
    if (!rendered) return undefined;
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
      // iOS WebKit leaves position:fixed elements (the bottom nav, the chat
      // chip) anchored to the just-released body until a real scroll repaints
      // them. Nudge across two frames to force the re-anchor.
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY > 0 ? scrollY - 1 : 1);
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
      });
    };
  }, [rendered]);

  // ── escape closes ──
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ── drag the header down to dismiss ──
  const endDrag = useCallback((commit: boolean) => {
    const el = sheetRef.current;
    dragStartRef.current = null;
    if (el) {
      el.style.transition = "";
      el.style.transform = "";
    }
    if (commit) onClose();
    dragDeltaRef.current = 0;
  }, [onClose]);

  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragStartRef.current = e.clientY;
    dragDeltaRef.current = 0;
    const el = sheetRef.current;
    if (el) el.style.transition = "none";
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }, []);

  const onHeaderPointerMove = useCallback((e: React.PointerEvent) => {
    if (dragStartRef.current === null) return;
    const delta = Math.max(0, e.clientY - dragStartRef.current);
    dragDeltaRef.current = delta;
    const el = sheetRef.current;
    if (el) el.style.transform = `translateY(${delta}px)`;
  }, []);

  const onHeaderPointerUp = useCallback(() => {
    if (dragStartRef.current === null) return;
    endDrag(dragDeltaRef.current > DRAG_CLOSE_PX);
  }, [endDrag]);

  if (!rendered) return null;

  return (
    <>
      <div
        className={"agent-chat-scrim" + (shown ? " is-in" : "")}
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={sheetRef}
        className={
          "agent-chat-sheet" +
          (shown ? " is-in" : "") +
          (keyboardOpen ? " is-keyboard" : "")
        }
        role="dialog"
        aria-modal="true"
        aria-label="sitebot chat"
      >
        <div
          className="agent-chat-sheet__grip"
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          onPointerCancel={onHeaderPointerUp}
        >
          <span className="agent-chat-sheet__grabber" aria-hidden />
          <button
            type="button"
            className="agent-chat-sheet__close"
            onClick={onClose}
            aria-label="close chat"
          >
            ✕
          </button>
        </div>
        <div className="agent-chat-sheet__body">{children}</div>
      </div>
    </>
  );
};

export default AgentChatSheet;
